import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Select,
  Button,
  Text,
  InlineStack,
  BlockStack,
  Box,
  TextField,
  Checkbox,
  Divider,
  Icon,
  Tooltip
} from '@shopify/polaris';
import {
  TextIcon,
  CodeIcon,
  HashtagIcon,
  ImageIcon,
  ResetIcon,
  SaveIcon
} from '@shopify/polaris-icons';

// Element types available for drag and drop
const ELEMENT_TYPES = [
  { id: 'title', name: 'Product Title', icon: TextIcon },
  { id: 'price', name: 'Price', icon: HashtagIcon },
  { id: 'comparePrice', name: 'Compare Price', icon: HashtagIcon },
  { id: 'barcode', name: 'Barcode', icon: CodeIcon },
  { id: 'sku', name: 'SKU', icon: TextIcon },
  { id: 'variant', name: 'Variant', icon: TextIcon },
  { id: 'text', name: 'Custom Text', icon: TextIcon }
];

function LabelDesigner({
  presets,
  selectedPreset,
  onPresetChange,
  sampleProduct,
  templates = [],
  onTemplatesChange
}) {
  const canvasRef = useRef(null);
  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 252, height: 81 });
  const [scale, setScale] = useState(2);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [loadedTemplateId, setLoadedTemplateId] = useState('');

  // Update canvas size when preset changes
  useEffect(() => {
    const preset = presets.find(p => p.id === selectedPreset);
    if (preset) {
      setCanvasSize({ width: preset.width, height: preset.height });
      // Reset elements when preset changes
      setElements(getDefaultElements(preset));
    }
  }, [selectedPreset, presets]);

  // Get default elements based on preset size
  const getDefaultElements = (preset) => {
    const isSmall = preset.height < 60;
    const isMedium = preset.height >= 60 && preset.height < 120;

    if (isSmall) {
      return [
        { id: 'title-1', type: 'title', x: 70, y: 5, width: preset.width - 80, fontSize: 8 },
        { id: 'price-1', type: 'price', x: 70, y: 18, fontSize: 10, bold: true },
        { id: 'barcode-1', type: 'barcode', x: 5, y: 5, width: 60, height: preset.height - 15 }
      ];
    }

    if (isMedium) {
      return [
        { id: 'title-1', type: 'title', x: 5, y: 5, width: preset.width - 15, fontSize: 10 },
        { id: 'barcode-1', type: 'barcode', x: (preset.width - 100) / 2, y: 20, width: 100, height: 35 },
        { id: 'price-1', type: 'price', x: 5, y: preset.height - 20, fontSize: 12, bold: true },
        { id: 'sku-1', type: 'sku', x: preset.width - 60, y: preset.height - 20, fontSize: 8 }
      ];
    }

    // Large
    return [
      { id: 'title-1', type: 'title', x: 10, y: 10, width: preset.width - 25, fontSize: 14 },
      { id: 'variant-1', type: 'variant', x: 10, y: 28, fontSize: 10, color: '#666666' },
      { id: 'barcode-1', type: 'barcode', x: (preset.width - 150) / 2, y: 45, width: 150, height: 50 },
      { id: 'price-1', type: 'price', x: 10, y: preset.height - 35, fontSize: 16, bold: true },
      { id: 'comparePrice-1', type: 'comparePrice', x: 80, y: preset.height - 35, fontSize: 10, strikethrough: true },
      { id: 'sku-1', type: 'sku', x: preset.width - 80, y: preset.height - 35, fontSize: 9 }
    ];
  };

  // Handle mouse down on element
  const handleMouseDown = useCallback((e, element) => {
    e.stopPropagation();
    setSelectedElement(element.id);
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const startX = e.clientX - rect.left;
    const startY = e.clientY - rect.top;
    
    setDragging({
      elementId: element.id,
      startX,
      startY,
      elementX: element.x,
      elementY: element.y
    });
  }, []);

  // Handle mouse move for dragging
  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;
      
      const deltaX = (currentX - dragging.startX) / scale;
      const deltaY = (currentY - dragging.startY) / scale;
      
      setElements(prev => prev.map(el => {
        if (el.id === dragging.elementId) {
          return {
            ...el,
            x: Math.max(0, Math.min(canvasSize.width - (el.width || 50), dragging.elementX + deltaX)),
            y: Math.max(0, Math.min(canvasSize.height - 20, dragging.elementY + deltaY))
          };
        }
        return el;
      }));
    };

    const handleMouseUp = () => {
      setDragging(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, scale, canvasSize]);

  // Add new element
  const handleAddElement = useCallback((type) => {
    const elementType = ELEMENT_TYPES.find(t => t.id === type);
    const newElement = {
      id: `${type}-${Date.now()}`,
      type,
      x: 10,
      y: 10,
      fontSize: 10,
      ...(type === 'barcode' && { width: 80, height: 30 }),
      ...(type === 'title' && { width: canvasSize.width - 20 }),
      ...(type === 'text' && { content: 'Custom Text' })
    };
    
    setElements(prev => [...prev, newElement]);
    setSelectedElement(newElement.id);
  }, [canvasSize]);

  // Delete selected element
  const handleDeleteElement = useCallback(() => {
    if (selectedElement) {
      setElements(prev => prev.filter(el => el.id !== selectedElement));
      setSelectedElement(null);
    }
  }, [selectedElement]);

  // Update element property
  const handleUpdateElement = useCallback((property, value) => {
    if (!selectedElement) return;
    
    setElements(prev => prev.map(el => {
      if (el.id === selectedElement) {
        return { ...el, [property]: value };
      }
      return el;
    }));
  }, [selectedElement]);

  // Get sample value for element type
  const getSampleValue = (type) => {
    if (!sampleProduct) return '';
    
    switch (type) {
      case 'title': return sampleProduct.title;
      case 'price': return `$${parseFloat(sampleProduct.price).toFixed(2)}`;
      case 'comparePrice': return sampleProduct.comparePrice ? `$${parseFloat(sampleProduct.comparePrice).toFixed(2)}` : '';
      case 'sku': return `SKU: ${sampleProduct.sku}`;
      case 'barcode': return sampleProduct.barcode || 'SAMPLE-123';
      case 'variant': return 'Size: Medium';
      // 'text' intentionally falls through to '' so the render falls back
      // to the element's own `content` (user-edited value).
      case 'text': return '';
      default: return '';
    }
  };

  const selectedEl = elements.find(el => el.id === selectedElement);
  const presetOptions = presets.map(p => ({ label: p.name, value: p.id }));

  // Save the current canvas as a template
  const handleSaveTemplate = useCallback(async () => {
    const name = window.prompt('Template name:', `${selectedPreset} layout`);
    if (!name) return;

    setSavingTemplate(true);
    try {
      const response = await fetch('/api/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, preset: selectedPreset, elements })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Could not save template');
      }
      setLoadedTemplateId(data.template.id);
      if (onTemplatesChange) onTemplatesChange();
    } catch (error) {
      console.error('Save template error:', error);
      window.alert(`Could not save template: ${error.message}`);
    } finally {
      setSavingTemplate(false);
    }
  }, [elements, selectedPreset, onTemplatesChange]);

  // Load a saved template into the canvas
  const handleLoadTemplate = useCallback((templateId) => {
    setLoadedTemplateId(templateId);
    if (!templateId) return;
    const template = (templates || []).find((t) => String(t.id) === String(templateId));
    if (!template) return;
    try {
      const parsed = typeof template.elements === 'string'
        ? JSON.parse(template.elements)
        : template.elements;
      if (Array.isArray(parsed)) {
        // Ensure element ids for canvas interactions
        setElements(parsed.map((el, i) => ({ ...el, id: el.id || `el-${Date.now()}-${i}` })));
      }
      if (template.preset && template.preset !== selectedPreset) {
        onPresetChange(template.preset);
      }
    } catch (e) {
      console.error('Load template error:', e);
    }
  }, [templates, selectedPreset, onPresetChange]);

  // Delete the loaded template
  const handleDeleteTemplate = useCallback(async () => {
    if (!loadedTemplateId) return;
    if (!window.confirm('Delete this template?')) return;
    try {
      await fetch(`/api/templates/${encodeURIComponent(loadedTemplateId)}`, { method: 'DELETE' });
      setLoadedTemplateId('');
      if (onTemplatesChange) onTemplatesChange();
    } catch (error) {
      console.error('Delete template error:', error);
    }
  }, [loadedTemplateId, onTemplatesChange]);

  const templateOptions = [
    { label: 'Unsaved design', value: '' },
    ...(templates || []).map((t) => ({ label: t.name, value: String(t.id) }))
  ];

  return (
    <Box padding="400">
      <BlockStack gap="400">
        {/* Toolbar */}
        <InlineStack gap="400" align="space-between" blockAlign="center">
          <InlineStack gap="200">
            <Select
              label="Preset"
              labelHidden
              options={presetOptions}
              value={selectedPreset}
              onChange={onPresetChange}
            />
            <Select
              label="Template"
              labelHidden
              options={templateOptions}
              value={loadedTemplateId ? String(loadedTemplateId) : ''}
              onChange={handleLoadTemplate}
            />
            <Select
              label="Scale"
              labelHidden
              options={[
                { label: '100%', value: '1' },
                { label: '150%', value: '1.5' },
                { label: '200%', value: '2' },
                { label: '300%', value: '3' }
              ]}
              value={scale.toString()}
              onChange={(v) => setScale(parseFloat(v))}
            />
          </InlineStack>
          <InlineStack gap="200">
            {loadedTemplateId && (
              <Button tone="critical" onClick={handleDeleteTemplate}>
                Delete
              </Button>
            )}
            <Button icon={ResetIcon} onClick={() => { setLoadedTemplateId(''); setElements(getDefaultElements(presets.find(p => p.id === selectedPreset))); }}>
              Reset
            </Button>
            <Button variant="primary" icon={SaveIcon} loading={savingTemplate} onClick={handleSaveTemplate}>
              Save Template
            </Button>
          </InlineStack>
        </InlineStack>

        <InlineStack gap="400" align="start">
          {/* Element Palette */}
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text variant="headingSm">Elements</Text>
                <Divider />
                {ELEMENT_TYPES.map(type => (
                  <Button
                    key={type.id}
                    icon={type.icon}
                    onClick={() => handleAddElement(type.id)}
                    fullWidth
                    textAlign="left"
                  >
                    {type.name}
                  </Button>
                ))}
              </BlockStack>
            </Box>
          </Card>

          {/* Canvas */}
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text variant="headingSm">Preview</Text>
                <div className="preview-container">
                  <div
                    ref={canvasRef}
                    className="label-canvas"
                    style={{
                      width: canvasSize.width * scale,
                      height: canvasSize.height * scale,
                      position: 'relative'
                    }}
                    onClick={() => setSelectedElement(null)}
                  >
                    <div className="label-canvas-background" />
                    
                    {elements.map(element => (
                      <div
                        key={element.id}
                        className={`label-element ${selectedElement === element.id ? 'selected' : ''}`}
                        style={{
                          left: element.x * scale,
                          top: element.y * scale,
                          width: element.width ? element.width * scale : 'auto',
                          height: element.height ? element.height * scale : 'auto',
                          fontSize: (element.fontSize || 10) * scale,
                          fontWeight: element.bold ? 'bold' : 'normal',
                          color: element.color || '#000',
                          textDecoration: element.strikethrough ? 'line-through' : 'none',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        onMouseDown={(e) => handleMouseDown(e, element)}
                      >
                        {element.type === 'barcode' ? (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              background: 'repeating-linear-gradient(90deg, #000 0px, #000 2px, #fff 2px, #fff 4px)',
                              display: 'flex',
                              alignItems: 'flex-end',
                              justifyContent: 'center',
                              fontSize: 8 * scale,
                              paddingBottom: 2
                            }}
                          >
                            <span style={{ background: 'white', padding: '0 2px' }}>
                              {getSampleValue('barcode')}
                            </span>
                          </div>
                        ) : (
                          getSampleValue(element.type) || element.content
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </BlockStack>
            </Box>
          </Card>

          {/* Properties Panel */}
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text variant="headingSm">Properties</Text>
                <Divider />
                {selectedEl ? (
                  <BlockStack gap="300">
                    <TextField
                      label="X Position"
                      type="number"
                      value={selectedEl.x?.toString() || '0'}
                      onChange={(v) => handleUpdateElement('x', parseInt(v) || 0)}
                      autoComplete="off"
                    />
                    <TextField
                      label="Y Position"
                      type="number"
                      value={selectedEl.y?.toString() || '0'}
                      onChange={(v) => handleUpdateElement('y', parseInt(v) || 0)}
                      autoComplete="off"
                    />
                    {selectedEl.type !== 'barcode' && (
                      <TextField
                        label="Font Size"
                        type="number"
                        value={selectedEl.fontSize?.toString() || '10'}
                        onChange={(v) => handleUpdateElement('fontSize', parseInt(v) || 10)}
                        autoComplete="off"
                      />
                    )}
                    {(selectedEl.type === 'barcode' || selectedEl.type === 'title') && (
                      <TextField
                        label="Width"
                        type="number"
                        value={selectedEl.width?.toString() || '100'}
                        onChange={(v) => handleUpdateElement('width', parseInt(v) || 100)}
                        autoComplete="off"
                      />
                    )}
                    {selectedEl.type === 'barcode' && (
                      <TextField
                        label="Height"
                        type="number"
                        value={selectedEl.height?.toString() || '30'}
                        onChange={(v) => handleUpdateElement('height', parseInt(v) || 30)}
                        autoComplete="off"
                      />
                    )}
                    {selectedEl.type === 'text' && (
                      <TextField
                        label="Content"
                        value={selectedEl.content || ''}
                        onChange={(v) => handleUpdateElement('content', v)}
                        autoComplete="off"
                      />
                    )}
                    <Checkbox
                      label="Bold"
                      checked={selectedEl.bold || false}
                      onChange={(v) => handleUpdateElement('bold', v)}
                    />
                    <Divider />
                    <Button tone="critical" onClick={handleDeleteElement}>
                      Delete Element
                    </Button>
                  </BlockStack>
                ) : (
                  <Text variant="bodySm" tone="subdued">
                    Select an element to edit its properties
                  </Text>
                )}
              </BlockStack>
            </Box>
          </Card>
        </InlineStack>
      </BlockStack>
    </Box>
  );
}

export default LabelDesigner;
